import React, { useState, useEffect, useCallback, useRef } from 'react'
import { apiAgent } from '../../../axiosConfig.js'
import { InputBox } from '../../../design_system/components/Input Fields/InputBox.jsx'
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx'
import { useSettingsContext, settingsSnapshot } from './SettingsContext'
import { Loader } from '../../../design_system/components/Loader.jsx'

const ManageProfile = (props) => {
  const toast = useToast();
  const { registerTab, unregisterTab, setHasChanges } = useSettingsContext()
  const snapshotRef = useRef(null)

  const [userDetails, setUserDetails] = useState({
    name: null,
    email: null,
    phone: null,
    position: null,
    institute: null,
  })
  const [errors, setErrors] = useState({})
  const [isDataLoaded, setIsDataLoaded] = useState(false)


  useEffect(() => {
    props.setActiveSettingTab('profile')
    apiAgent({
      method: 'get',
      url: '/users/profile',
      params: {}
    }).then(res => {
      if (res.data.user) {
        setUserDetails(res.data.user)
        snapshotRef.current = settingsSnapshot(res.data.user)
        setIsDataLoaded(true)
      }
    }).catch(() => {})
  }, [])

  const onGeneralInputChange = (e) => {
    setUserDetails({ ...userDetails, [e.target.name]: e.target.value })
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' })
  }

  // ── Change detection (General section only) ──────────────────────────────

  useEffect(() => {
    if (snapshotRef.current === null) return
    setHasChanges(settingsSnapshot(userDetails) !== snapshotRef.current)
  }, [userDetails, setHasChanges])

  const onGeneralDetailsSubmit = useCallback(() => {
    let errs = {}
    if (!userDetails.email) errs.email = "Email can't be blank"
    if (Object.keys(errs).length > 0) { setErrors(errs); return Promise.reject() }
    return apiAgent({
      method: 'post',
      url: '/users/update_profile',
      data: { userDetails }
    }).then(() => {
      toast.success("Changes saved")
      snapshotRef.current = settingsSnapshot(userDetails)
      setHasChanges(false)
    }).catch(() => {
      toast.error("Failed to save changes")
    })
  }, [userDetails, toast, setHasChanges])

  const handleCancel = useCallback(() => {
    if (snapshotRef.current) setUserDetails(JSON.parse(snapshotRef.current))
    setErrors({})
  }, [])

  const submitRef = useRef(onGeneralDetailsSubmit)
  const cancelCallbackRef = useRef(handleCancel)
  submitRef.current = onGeneralDetailsSubmit
  cancelCallbackRef.current = handleCancel

  useEffect(() => {
    const id = registerTab({
      onSave: (...args) => submitRef.current(...args),
      onCancel: (...args) => cancelCallbackRef.current(...args),
    })
    return () => unregisterTab(id)
  }, [registerTab, unregisterTab])


  return (
    <div className="settings-panel">
      <div className="settings-panel__header">
        <div className="settings-panel__title body-l-bold">Profile</div>
        <div className="settings-panel__description body-s-semibold">Manage your personal information and account preferences.</div>
      </div>

      {!isDataLoaded ? <Loader isLoading Size="Medium" /> : <>

      {/* General */}
      <div className="settings-panel__section">
        <div className="settings-panel__section-title body-m-bold">General</div>
        <div style={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 'var(--space-s)', marginTop: 'var(--space-m)' }}>
          <InputBox label="Name" showLabel value={userDetails.name || ''} name="name"
            handleOnChange={onGeneralInputChange} placeholder="John Doe" error={errors.name} />
          <InputBox label="Email" showLabel value={userDetails.email || ''} name="email"
            handleOnChange={onGeneralInputChange} placeholder="johndoe@domain.com" disabled />
          <InputBox label="Phone" showLabel value={userDetails.phone || ''} name="phone"
            handleOnChange={onGeneralInputChange} placeholder="+1 (555) 555-5555" error={errors.phone} />
          <InputBox label="Title / Position" showLabel value={userDetails.position || ''} name="position"
            handleOnChange={onGeneralInputChange} placeholder="Director" error={errors.position} />
          <InputBox label="Institute" showLabel value={userDetails.institute || ''} name="institute"
            handleOnChange={onGeneralInputChange} placeholder="New York University" disabled />
        </div>
      </div>

      </>}
    </div>
  )
}

export default ManageProfile
